import keys from "../config/env";
import {
  toSettings,
  toUser,
  toVideoCategory,
  toVimeoVideoCategory,
  toCustomer,
  getStripe,
  toVimeoVideo,
  toEmail,
  toCommunication,
  toGenericObjective,
  toActivity,
  toReservation,
  toEvaluation,
  toPlanning,
} from "./utils";
import {
  Activity,
  BookingEvents,
  Comunication,
  Email,
  Evaluation,
  GenericObjective,
  Payment,
  Planning,
  Reservation,
  Settings,
  Subscription,
  User,
  VideoCategory,
  VimeoVideo,
  VimeoVideoCategory,
} from "../types";
import storageServices from "./storage";
import dayjs from "dayjs";
import env from "../config/env";

let sessionToken: string | undefined;

const setSession = (token: string) => {
  sessionToken = token;
};

const clearSession = () => {
  sessionToken = undefined;
};

const makeApiRequest = async (
  method: "POST" | "GET" | "PATCH" | "DELETE",
  path: string,
  body?: any
): Promise<any> => {
  const headers = new Headers({
    Accept: "application/json",
    "Content-Type": "application/json",
    mode: "no-cors",
  });
  if (sessionToken) {
    headers.append("Authorization", `Bearer ${sessionToken}`);
  }

  try {
    const response = await fetch(`${keys.API_BASE_URL}${path}`, {
      method: method,
      body: body ? JSON.stringify(body) : undefined,
      headers: headers,
    });

    if (!response.ok) {
      const message = await parseErrorMessage(response);
      throw new Error(`${response.status}: ${response.statusText}. ${message}`);
    }
    const responseJson = await response.json();
    return responseJson;
  } catch (err) {
    console.warn(`Failed request ${method} ${path}`, err);
    throw err;
  }
};

const parseErrorMessage = async (response: Response) => {
  try {
    const data = await response.json();
    if (!data.ErrorMessage) {
      throw new Error();
    }
    return data.ErrorMessage;
  } catch (err) {
    return "";
  }
};

const mapToSubscription = (data: any): Subscription => {
  return {
    id: data.id,
    cancel_at: data.cancel_at ? new Date(data.cancel_at * 1000) : undefined,
    cancel_at_period_end: data.cancel_at_period_end,
    canceled_at: data.canceled_at
      ? new Date(data.canceled_at * 1000)
      : undefined,
    created: new Date(data.created * 1000),
    current_period_end: new Date(data.current_period_end * 1000),
    current_period_start: new Date(data.current_period_start * 1000),
    customer: data.customer,
    days_until_due: data.days_until_due,
    ended_at: data.ended_at ? new Date(data.ended_at * 1000) : undefined,
    start_date: new Date(data.start_date * 1000),
    plan: {
      id: data.plan.id,
      product: data.plan.product,
      amount: data.plan.amount / 100,
      productName: data.plan.productName,
    },
  };
};

const mapToPayments = (data: any): Payment[] => {
  return data.map((payment: any): Payment => {
    return {
      id: payment.id,
      amount: payment.amount / 100,
      currency: payment.currency,
      created: new Date(payment.created * 1000),
      customer: payment.customer,
      paid: payment.paid,
      refunded: payment.refunded,
      status: payment.status,
    };
  });
};

const login = async (email: User["email"], password: string): Promise<User> => {
  // delete old token
  storageServices.removeItem("session");
  const body = {
    email,
    password,
  };
  const response = await makeApiRequest("POST", "auth/basic", body);

  storageServices.setItem("session", { token: response.token });
  setSession(response.token);
  return toUser(response.user);
};

const signUp = async (
  name: User["name"],
  surname: User["surname"],
  email: User["email"],
  password: string,
  phone: User["phone"]
): Promise<User> => {
  // delete old token
  storageServices.removeItem("session");
  const body = {
    name,
    surname,
    email,
    password,
    phone,
  };
  const response = await makeApiRequest("POST", "auth/basic/signup", body);

  storageServices.setItem("session", { token: response.token });
  setSession(response.token);
  return toUser(response.user);
};

const getCurrentUser = async (): Promise<User | null> => {
  const sessionData: { token: string } = storageServices.getItem("session") as {
    token: string;
  };

  if (!sessionData) return null;
  setSession(sessionData.token);
  const result = await makeApiRequest("GET", `user/me`);

  return toUser(result);
};

const lostPassword = async (email: User["email"]): Promise<boolean> => {
  await makeApiRequest(
    "GET",
    `auth/basic/lost-password?email=${encodeURIComponent(email)}`
  );

  return true;
};

const changePassword = async (
  email: User["email"],
  password: string,
  token: string
): Promise<boolean> => {
  await makeApiRequest("POST", "auth/basic/change-password", {
    email,
    password,
    token,
  });

  return true;
};

const changeSessionUserPassword = async (
  email: User["email"],
  password: string
): Promise<boolean> => {
  await makeApiRequest("PATCH", "auth/basic/change-password", {
    email,
    password,
  });

  return true;
};

const stripeSessionCheckout = async (sessionId: string): Promise<any> => {
  const response = await makeApiRequest("POST", "stripe/checkout/get", {
    sessionId: sessionId,
  });

  if (response.statusCode === 500) {
    throw new Error(response.message);
  }

  return response.json;
};

const stripeCheckout = async (email: User["email"]): Promise<boolean> => {
  const response = await makeApiRequest("POST", "stripe/checkout/add", {
    email,
    priceId:
      env.NODE_ENV === "development"
        ? env.STRIPE_TEST_PRODUCT
        : env.STRIPE_LIVE_PRODUCT,
  });

  if (response.statusCode === 500) {
    throw new Error(response.message);
  }

  // await stripeSessionCheckout(response.id);

  // Redirect to Checkout.
  const stripe = await getStripe();
  const { error } = await stripe!.redirectToCheckout({
    sessionId: response.id,
  });
  if (error) {
    throw new Error(error.message);
  }

  return true;
};

const stripeAccessPortal = async (customerId: string): Promise<string> => {
  const response = await makeApiRequest("POST", "stripe/portal/access", {
    customerId: customerId,
  });

  if (response.statusCode === 500) {
    throw new Error(response.message);
  }

  return response.url;
};

const stripeGetSubscription = async (subscriptionId: string): Promise<any> => {
  const response = await makeApiRequest("POST", "stripe/subscription/get", {
    subscriptionId: subscriptionId,
  });

  if (response.statusCode === 500) {
    throw new Error(response.message);
  }

  return mapToSubscription(response);
};

const stripeGetPayments = async (customerId: string): Promise<Payment[]> => {
  const response = await makeApiRequest("POST", "stripe/payments/get", {
    customerId: customerId,
  });

  if (response.statusCode === 500) {
    throw new Error(response.message);
  }

  return mapToPayments(response.data);
};

// Get All video Categories from Vimeo
const getVimeoVideoCategories = async (): Promise<VimeoVideoCategory[]> => {
  const response = await makeApiRequest("GET", "/admin/vimeo/folder");
  return response.map((item: any) => toVimeoVideoCategory(item));
};

const getVideoCategories = async (): Promise<VideoCategory[]> => {
  const response = await makeApiRequest("GET", "/video-category");

  return response.map((item: any) => toVideoCategory(item));
};

const addVideoCategories = async (
  videoCategory: Partial<VideoCategory>
): Promise<VideoCategory> => {
  const response = await makeApiRequest(
    "POST",
    "/add-video-category",
    videoCategory
  );

  return toVideoCategory(response);
};

const updateVideoCategories = async (
  videoCategory: Partial<VideoCategory>
): Promise<VideoCategory> => {
  const response = await makeApiRequest(
    "POST",
    "/update-video-category",
    videoCategory
  );

  return toVideoCategory(response);
};

const getSettings = async (): Promise<Settings[]> => {
  const response = await makeApiRequest("GET", `/settings`);

  return response.map((item: any) => toSettings(item));
};

const getSetting = async (key: Settings["key"]): Promise<Settings> => {
  const response = await makeApiRequest("GET", `/settings?key=${key}`);

  return toSettings(response);
};

const updateSettings = async (settings: Settings): Promise<Settings> => {
  const response = await makeApiRequest("PATCH", "/admin/settings", settings);

  return toSettings(response);
};

const getClients = async (): Promise<User[]> => {
  let response = await makeApiRequest("GET", "customer");

  return response.map((item: any) => toCustomer(item));
};

const getClient = async (user_id: string): Promise<any> => {
  let response = await makeApiRequest("GET", "customer/" + user_id);
  return toCustomer(response);
};

const deleteClient = async (userId: User["id"]): Promise<boolean> => {
  await makeApiRequest("DELETE", `customer/${userId}`);
  return true;
};

const getClientsSubscriptionCancellation = async (): Promise<any> => {
  let response = await makeApiRequest(
    "GET",
    "customer/subscription-cancellation"
  );
  return response.map((item: any) => toCustomer(item));
};

const getClientsEmptyWeight = async (): Promise<any> => {
  let response = await makeApiRequest("GET", "customer/empty-weight");
  return response.map((item: any) => toCustomer(item));
};

const getClientsEmptyEvaluation = async (): Promise<any> => {
  let response = await makeApiRequest("GET", "customer/empty-evaluation");
  return response.map((item: any) => toCustomer(item));
};

const getClientsSubscriptionTrial = async (): Promise<any> => {
  let response = await makeApiRequest("GET", "customer/subscription-trial");
  return response.map((item: any) => toCustomer(item));
};

const getClientsFewWorkouts = async (numDays: number): Promise<any> => {
  const dateLimit = dayjs().subtract(numDays, "days");

  let response = await makeApiRequest(
    "GET",
    `customer/few-workouts?startTime=${dateLimit.toISOString()}`
  );
  return response;
};

const getAdmins = async (): Promise<User[]> => {
  let response = await makeApiRequest("GET", "admins");
  return response.map((item: any) => toCustomer(item));
};

const updateCustomerPersonalData = async (
  user_id: User["id"],
  data: Partial<User>
): Promise<any> => {
  let response = await makeApiRequest("PATCH", `customer/${user_id}`, data);
  return toCustomer(response);
};

// EMAIL
const getEmail = async (userId: string): Promise<Email[]> => {
  let response = await makeApiRequest("GET", `/customer/${userId}/email`);
  return response.map((item: any) => toEmail(item));
};

const addEmail = async (data: Partial<Email>): Promise<Email> => {
  let response = await makeApiRequest(
    "POST",
    `/customer/${data.userId}/email`,
    data
  );
  return toEmail(response);
};

const updateEmail = async (data: Partial<Email>): Promise<Email> => {
  let response = await makeApiRequest(
    "PATCH",
    `/customer/${data.userId}/email/${data.id}`,
    data
  );
  return toEmail(response);
};

const deleteEmail = async (data: Partial<Email>): Promise<any> => {
  let response = await makeApiRequest(
    "DELETE",
    `/customer/${data.userId}/email/${data.id}`
  );
  return response;
};

// COMUNICATIONS
const getComunication = async (userId: string): Promise<Comunication[]> => {
  let response = await makeApiRequest(
    "GET",
    `/customer/${userId}/comunications`
  );
  return response.map((item: any) => toCommunication(item));
};

const addComunication = async (data: any): Promise<Comunication> => {
  let response = await makeApiRequest(
    "POST",
    "comunications/add-comunication",
    data
  );
  return toCommunication(response);
};

const updateComunication = async (data: any): Promise<Comunication> => {
  let response = await makeApiRequest(
    "POST",
    "comunications/update-comunication",
    data
  );
  return toCommunication(response);
};

const deleteComunication = async (data: any): Promise<Comunication> => {
  let response = await makeApiRequest(
    "DELETE",
    "comunications/delete-comunication",
    data
  );
  return toCommunication(response);
};

//PERSONAL OBJECTIVE
const getPersonalObjective = async (userId: string): Promise<any> => {
  let response = await makeApiRequest(
    "GET",
    `customer/${userId}/personal-objective`
  );
  return response;
};

const addPersonalObjective = async (data: any): Promise<any> => {
  let response = await makeApiRequest(
    "POST",
    `customer/${data.userId}/personal-objective`,
    data
  );
  return response;
};

const updatePersonalObjective = async (data: any): Promise<any> => {
  let response = await makeApiRequest(
    "POST",
    `customer/${data.userId}/personal-objective/${data.id}`,
    data
  );
  return response;
};

//GENERIC OBJECTIVE
const getGenericObjective = async (
  userId: string
): Promise<GenericObjective[]> => {
  let response = await makeApiRequest(
    "GET",
    `customer/${userId}/generic-objectives`
  );
  return response.map((item: any) => toGenericObjective(item));
};

const getGenericObjectiveById = async (id: string): Promise<any> => {
  let response = await makeApiRequest("GET", "generic-objective/id/" + id);
  return response;
};

const addGenericObjective = async (
  data: Partial<GenericObjective>
): Promise<GenericObjective> => {
  let response = await makeApiRequest(
    "POST",
    `customer/${data.userId}/generic-objectives`,
    data
  );
  return toGenericObjective(response);
};

const updateGenericObjective = async (
  data: Partial<GenericObjective>
): Promise<GenericObjective> => {
  let response = await makeApiRequest(
    "PATCH",
    `customer/${data.userId}/generic-objectives/${data.id}`,
    data
  );
  return toGenericObjective(response);
};

const getVideoList = async (
  categoryExternalId: VideoCategory["externalId"]
): Promise<VimeoVideo[]> => {
  const response = await makeApiRequest(
    "GET",
    `video?categoryExternalId=${categoryExternalId}`
  );

  return response.map((item: any) => toVimeoVideo(item));
};

// EVALUATION
const getEvaluation = async (user_id: string): Promise<Evaluation[]> => {
  const response = await makeApiRequest("GET", "evaluation/" + user_id);
  return response.map((item: any) => toEvaluation(item));
};

const addEvaluation = async (data: any): Promise<any> => {
  let response = await makeApiRequest(
    "POST",
    "evaluation/add-evaluation",
    data
  );

  return toEvaluation(response);
};

const updateEvaluation = async (data: any): Promise<any> => {
  let response = await makeApiRequest(
    "POST",
    "evaluation/update-evaluation",
    data
  );

  return toEvaluation(response);
};

const deleteEvaluation = async (data: any): Promise<any> => {
  let response = await makeApiRequest(
    "DELETE",
    "evaluation/delete-evaluation",
    data
  );
  return response;
};

// Activity
const traceActivity = async (data: Partial<Activity>): Promise<Activity> => {
  let response = await makeApiRequest("POST", "/trace/activity", data);

  return toActivity(response);
};

const getActivities = async (
  userId: User["id"],
  fromDate: Date,
  toDate: Date
): Promise<Activity[]> => {
  let response = await makeApiRequest(
    "GET",
    `/customer/${userId}/activity?startTime=${fromDate.toISOString()}&endTime=${toDate.toISOString()}`
  );

  return response.map((item: any) => toActivity(item));
};

const removeActivity = async (id: number) => {
  let response = await makeApiRequest("DELETE", `activity/` + id);

  return toActivity(response);
};

// Reservation
const traceReservation = async (
  data: Partial<Reservation> & { bookingEvent?: BookingEvents }
): Promise<Reservation> => {
  let response = await makeApiRequest("POST", "/trace/reservation", data);

  return toReservation(response);
};

const getReservations = async (
  userId: User["id"],
  fromDate: Date,
  toDate: Date
): Promise<Reservation[]> => {
  let response = await makeApiRequest(
    "GET",
    `/customer/${userId}/reservation?startTime=${fromDate.toISOString()}&endTime=${toDate.toISOString()}`
  );

  return response.map((item: any) => toReservation(item));
};

const removeReservation = async (id: number) => {
  let response = await makeApiRequest("DELETE", `reservation/` + id);

  return toReservation(response);
};

const addCustomer = async (user: Partial<User>): Promise<User> => {
  const response = await makeApiRequest("POST", "admin/customer", user);
  return toUser(response);
};

const addAdmin = async (user: Partial<User>): Promise<User> => {
  const response = await makeApiRequest("POST", "admin/add-admin", user);
  return toUser(response);
};

const deleteAdmin = async (user_id: User["id"]): Promise<any> => {
  let response = await makeApiRequest(
    "DELETE",
    `admin/delete-admin/${user_id}`
  );
  return response;
};

const getZoomSignature = async (meetingId: string): Promise<string> => {
  let response = await makeApiRequest(
    "POST",
    `/zoom/join/${meetingId}/signature`,
    {}
  );

  return response.signature;
};

const generateExportFile = async (data: any[][]): Promise<Blob> => {
  try {
    const headers = new Headers({
      Accept: "application/json",
      "Content-Type": "application/json",
      mode: "no-cors",
    });

    const response = await fetch(`${keys.API_BASE_URL}/export`, {
      method: "POST",
      body: JSON.stringify({ data }),
      headers,
    });

    if (!response.ok) {
      const message = await parseErrorMessage(response);
      throw new Error(`${response.status}: ${response.statusText}. ${message}`);
    }

    return response.blob();
  } catch (err) {
    throw err;
  }
};

// Planning
const getPlannings = async (
  userId: User["id"],
  fromDate: Date,
  toDate: Date
): Promise<Planning[]> => {
  let response = await makeApiRequest(
    "GET",
    `/customer/${userId}/planning?startTime=${fromDate.toISOString()}&endTime=${toDate.toISOString()}`
  );

  return response.map((item: any) => toPlanning(item));
};

const addPlanning = async (
  userId: User["id"],
  data: Partial<Planning>
): Promise<Planning> => {
  let response = await makeApiRequest(
    "POST",
    `/customer/${userId}/planning`,
    data
  );

  return toPlanning(response);
};

const updatePlanning = async (
  userId: User["id"],
  id: Planning["id"],
  data: Partial<Planning>
): Promise<Planning> => {
  let response = await makeApiRequest(
    "PATCH",
    `/customer/${userId}/planning/${id}`,
    data
  );

  return toPlanning(response);
};

const removePlanning = async (userId: User["id"], id: Planning["id"]) => {
  let response = await makeApiRequest(
    "DELETE",
    `/customer/${userId}/planning/${id}`
  );

  return toPlanning(response);
};

const api = {
  setSession,
  clearSession,
  login,
  signUp,
  getCurrentUser,
  lostPassword,
  changePassword,
  changeSessionUserPassword,
  stripeCheckout,
  stripeSessionCheckout,
  stripeAccessPortal,
  stripeGetSubscription,
  stripeGetPayments,
  getVimeoVideoCategories,
  getVideoCategories,
  addVideoCategories,
  updateVideoCategories,
  getSettings,
  getSetting,
  updateSettings,
  getClients,
  getClientsSubscriptionCancellation,
  getClientsEmptyWeight,
  getClientsEmptyEvaluation,
  getClientsSubscriptionTrial,
  getClientsFewWorkouts,
  getAdmins,
  getClient,
  updateCustomerPersonalData,
  getEmail,
  addEmail,
  updateEmail,
  deleteEmail,
  getComunication,
  addComunication,
  updateComunication,
  deleteComunication,
  getPersonalObjective,
  addPersonalObjective,
  updatePersonalObjective,
  getGenericObjective,
  getGenericObjectiveById,
  addGenericObjective,
  updateGenericObjective,
  getVideoList,
  getEvaluation,
  addEvaluation,
  updateEvaluation,
  deleteEvaluation,
  traceActivity,
  getActivities,
  traceReservation,
  getReservations,
  addCustomer,
  addAdmin,
  deleteAdmin,
  getZoomSignature,
  removeActivity,
  removeReservation,
  deleteClient,
  generateExportFile,
  getPlannings,
  addPlanning,
  updatePlanning,
  removePlanning,
};

export default api;
